import React from 'react'
import { Card, Col, Row } from 'antd'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { EnergyMode } from '@vacationtracker/shared/types/user-insight'
import EnergyLevelImage from '../../EnergyLevelPage/components/energy-gauge/energy-level-image'
import DistributionBar from './DistributionBar'
import { IDepartmentDistribution } from '@vacationtracker/shared/types/team-energy'

interface IKpiTilesProps {
  energy: { score: number; mode: EnergyMode } | null
  needAttention: { count: number; total: number }
  avgDaysSincePto: number | null
  distribution: IDepartmentDistribution
  compact?: boolean
}

const MODE_I18N_KEY: Record<EnergyMode, string> = {
  [EnergyMode.PEAK]: 'teamEnergy.status.peak',
  [EnergyMode.ENERGIZED]: 'teamEnergy.status.energized',
  [EnergyMode.FUNCTIONAL]: 'teamEnergy.status.functional',
  [EnergyMode.STRUGGLING]: 'teamEnergy.status.struggling',
  [EnergyMode.DRAINED]: 'teamEnergy.status.drained',
}

const KpiTiles: React.FC<IKpiTilesProps> = ({ energy, needAttention, avgDaysSincePto, distribution, compact = false }) => {
  const needAttentionClass = needAttention.count > 0 ? 'te-kpi-tile__primary te-kpi-tile__primary--alert' : 'te-kpi-tile__primary'
  // Compact only takes effect at xxl+ - below that the tiles stay 4-in-a-row
  // since the chart sits below them, not next to them.
  const colProps = compact ? { xs: 24 as const, sm: 12 as const, lg: 6 as const, xxl: 12 as const } : { xs: 24 as const, sm: 12 as const, lg: 6 as const }
  const rowClass = compact ? 'te-kpi-row te-kpi-row--compact' : 'te-kpi-row'
  return (
    <Row gutter={[16, 16]} align="stretch" className={rowClass}>
      <Col {...colProps}>
        <Card size="small" className="te-kpi-tile te-kpi-tile--energy">
          {energy ? (
            <>
              <div className="te-kpi-tile__energy-image">
                <EnergyLevelImage score={energy.score} />
              </div>
              <div className="te-kpi-tile__energy-text">
                <div className={`te-kpi-tile__mode-name te-kpi-tile__mode-name--${energy.mode}`}>
                  <IntlMessages id={MODE_I18N_KEY[energy.mode]} />
                </div>
                <div className="te-kpi-tile__caption">
                  <IntlMessages id="teamEnergy.kpi.teamEnergy" />
                </div>
              </div>
            </>
          ) : (
            <div className="te-kpi-tile__energy-text">
              <div className="te-kpi-tile__mode-name">
                <IntlMessages id="teamEnergy.kpi.noData" />
              </div>
              <div className="te-kpi-tile__caption">
                <IntlMessages id="teamEnergy.kpi.teamEnergy" />
              </div>
            </div>
          )}
        </Card>
      </Col>

      <Col {...colProps}>
        <Card size="small" className={`te-kpi-distribution${energy ? '' : ' te-kpi-distribution--empty'}`}>
          <DistributionBar distribution={distribution} />
        </Card>
      </Col>

      <Col {...colProps}>
        <Card size="small" className="te-kpi-tile">
          <div className={needAttentionClass}>
            {needAttention.count}
            <span className="te-kpi-tile__secondary"> / {needAttention.total}</span>
          </div>
          <div className="te-kpi-tile__caption">
            <IntlMessages id="teamEnergy.kpi.needAttention" />
          </div>
        </Card>
      </Col>

      <Col {...colProps}>
        <Card size="small" className="te-kpi-tile">
          <div className="te-kpi-tile__primary">{avgDaysSincePto != null ? `${avgDaysSincePto}d` : '-'}</div>
          <div className="te-kpi-tile__caption">
            <IntlMessages id="teamEnergy.kpi.avgSincePto" />
          </div>
        </Card>
      </Col>
    </Row>
  )
}

export default KpiTiles
